import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  People as PeopleIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useCanAccessAdmin, useIsAdmin, useHasRole, useCanManageLeague } from '../../../contexts/PermissionsContext';
import AdminLayout from '../../../layouts/AdminLayout';
import OrganizationMembersTable from '../../../components/admin/OrganizationMembersTable';
import unifiedApi from '../../../utils/unifiedApi';

const LeagueMembersPage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const canAccessAdmin = useCanAccessAdmin();
  const isAdmin = useIsAdmin();
  const isSuperAdmin = useHasRole('super-admin');

  const [viewFilter, setViewFilter] = useState('all');
  const [rosterFilter, setRosterFilter] = useState('both');
  const [statusFilter, setStatusFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [leagueTeams, setLeagueTeams] = useState([]);
  const [leagueName, setLeagueName] = useState('');
  const [loadingTeams, setLoadingTeams] = useState(true);
  const canManageLeague = useCanManageLeague(parseInt(leagueId));

  // Bulk invite state
  const [bulkInviteConfirmOpen, setBulkInviteConfirmOpen] = useState(false);
  const [outstandingCount, setOutstandingCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [bulkInviteMessage, setBulkInviteMessage] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchLeagueData = async () => {
      setLoadingTeams(true);
      try {
        const [leagueInfo, teamsData] = await Promise.all([
          unifiedApi.getLeagueById(leagueId),
          unifiedApi.get('/api/teams'),
        ]);

        if (leagueInfo) {
          setLeagueName(leagueInfo.name);
        }

        if (teamsData) {
          const teamsInLeague = teamsData.filter(
            (team) => team.leagueId === parseInt(leagueId)
          );
          setLeagueTeams(teamsInLeague);
        }
      } catch (err) {
        console.error('Error fetching league data:', err);
      } finally {
        setLoadingTeams(false);
      }
    };

    fetchLeagueData();
  }, [leagueId]);

  const handleBulkInviteClick = useCallback(async () => {
    setLoadingCount(true);
    setBulkInviteMessage(null);
    try {
      const result = await unifiedApi.getOutstandingInvitationCount(leagueId);
      setOutstandingCount(result?.outstandingCount ?? 0);
      setBulkInviteConfirmOpen(true);
    } catch (err) {
      setBulkInviteMessage({ type: 'error', text: 'Failed to load outstanding member count' });
    } finally {
      setLoadingCount(false);
    }
  }, [leagueId]);

  const handleBulkInviteConfirm = useCallback(async () => {
    setSendingBulk(true);
    setBulkInviteConfirmOpen(false);
    try {
      const result = await unifiedApi.sendAllLeagueInvitations(leagueId);
      const count = result?.queuedCount ?? 0;
      setBulkInviteMessage({
        type: 'success',
        text: `${count} invitation email${count !== 1 ? 's' : ''} queued for delivery.`,
      });
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      setBulkInviteMessage({ type: 'error', text: 'Failed to send bulk invitations' });
    } finally {
      setSendingBulk(false);
    }
  }, [leagueId]);

  const hasAccess = canAccessAdmin || isSuperAdmin;

  if (!hasAccess) {
    return (
      <AdminLayout>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="error">
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You don&apos;t have permission to access this area.
          </Typography>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/admin/leagues/${leagueId}`)}
          sx={{ mb: 2 }}
        >
          Back to League Admin
        </Button>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleIcon color="primary" />
          {leagueName ? `${leagueName} - Members` : 'League Members'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and manage members for this league.
        </Typography>
      </Box>

      {bulkInviteMessage && (
        <Alert
          severity={bulkInviteMessage.type}
          onClose={() => setBulkInviteMessage(null)}
          sx={{ mb: 2 }}
        >
          {bulkInviteMessage.text}
        </Alert>
      )}

      {canManageLeague && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={sendingBulk || loadingCount ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            onClick={handleBulkInviteClick}
            disabled={sendingBulk || loadingCount}
          >
            {sendingBulk ? 'Sending...' : 'Invite All Outstanding'}
          </Button>
        </Box>
      )}

      <Dialog open={bulkInviteConfirmOpen} onClose={() => setBulkInviteConfirmOpen(false)}>
        <DialogTitle>Send All Invitations</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {outstandingCount === 0
              ? 'There are no outstanding members to invite. All eligible members have been invited within the last 7 days.'
              : `This will send invitation emails to ${outstandingCount} member${outstandingCount !== 1 ? 's' : ''} who haven\u2019t accepted their Quailgame invitation. Members invited within the last 7 days will be skipped.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkInviteConfirmOpen(false)}>Cancel</Button>
          {outstandingCount > 0 && (
            <Button onClick={handleBulkInviteConfirm} variant="contained" autoFocus>
              Send {outstandingCount} Invitation{outstandingCount !== 1 ? 's' : ''}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>View Members</InputLabel>
          <Select
            value={viewFilter}
            label="View Members"
            onChange={(e) => setViewFilter(e.target.value)}
          >
            <MenuItem value="all">All Members</MenuItem>
            <MenuItem value="elevated">Elevated Permissions</MenuItem>
            <MenuItem value="admins">Admins Only</MenuItem>
            <MenuItem value="managers">Managers</MenuItem>
            <MenuItem value="players">Players Only</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Roster Status</InputLabel>
          <Select
            value={rosterFilter}
            label="Roster Status"
            onChange={(e) => setRosterFilter(e.target.value)}
          >
            <MenuItem value="both">All Players</MenuItem>
            <MenuItem value="rostered">Rostered</MenuItem>
            <MenuItem value="unrostered">Unrostered</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="not-invited">Not Invited</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Team</InputLabel>
          <Select
            value={teamFilter}
            label="Team"
            onChange={(e) => setTeamFilter(e.target.value)}
            disabled={loadingTeams}
          >
            <MenuItem value="all">All Teams</MenuItem>
            {leagueTeams.map((team) => (
              <MenuItem key={team.id} value={team.id.toString()}>
                {team.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loadingTeams ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <OrganizationMembersTable
          viewFilter={viewFilter}
          rosterFilter={rosterFilter}
          statusFilter={statusFilter}
          teamFilter={teamFilter}
          leagueFilter={leagueId}
          canEdit={isAdmin}
          refreshKey={refreshKey}
        />
      )}
    </AdminLayout>
  );
};

export default LeagueMembersPage;
